import React from 'react';
import type { BlockRenderProps } from './types';
import { EditableText } from './EditableText';

export const ContainerRenderer: React.FC<BlockRenderProps> = ({ node, children }) => {
  const maxWidth = (node.props?.maxWidth as string) || '1200px';
  const centered = node.props?.centered !== false;

  return (
    <div
      style={{
        maxWidth,
        margin: centered ? '0 auto' : undefined,
        width: '100%',
        boxSizing: 'border-box',
      }}
      className="wb-container"
    >
      {children || <div className="p-4 border border-dashed border-muted text-muted-foreground text-xs text-center rounded">Empty Container (Drop items here)</div>}
    </div>
  );
};

export const Grid2ColRenderer: React.FC<BlockRenderProps> = ({ node, children }) => {
  const gap = (node.props?.gap as string) || '24px';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap,
        width: '100%',
      }}
      className="wb-grid-2col"
    >
      {children || <div className="p-4 border border-dashed border-muted text-muted-foreground text-xs text-center rounded col-span-2">2-Column Grid (Drop items here)</div>}
    </div>
  );
};

export const SectionRenderer: React.FC<BlockRenderProps> = ({ node, children }) => {
  return (
    <section
      id={(node.props?.sectionId as string) || undefined}
      style={{
        width: '100%',
        padding: '32px 16px',
      }}
      className="wb-section"
    >
      {children}
    </section>
  );
};

export const SpacerRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const height = (node.props?.height as string) || '40px';
  return <div style={{ height }} aria-hidden="true" className="wb-spacer" />;
};

export const DividerRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const thickness = (node.props?.thickness as string) || '1px';
  const style = (node.props?.style as string) || 'solid';

  return (
    <hr
      style={{
        border: 'none',
        borderTop: `${thickness} ${style} #e5e7eb`,
        margin: '24px 0',
      }}
      className="wb-divider"
    />
  );
};

export const HeroSplitRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const headline = (node.props?.headline as string) || 'Empower Your Learning Journey';
  const subheadline = (node.props?.subheadline as string) || 'Join thousands of successful students achieving top ranks.';
  const ctaText = (node.props?.ctaText as string) || 'Explore Courses';
  const ctaLink = (node.props?.ctaLink as string) || '#';
  const imageURL = (node.props?.imageURL as string) || 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&auto=format&fit=crop&q=80';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center py-8 w-full">
      <div>
        <EditableText
          as="h1"
          value={headline}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { headline: newVal })}
          className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight mb-4 block"
        />
        <EditableText
          as="p"
          value={subheadline}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { subheadline: newVal })}
          className="text-base md:text-lg text-muted-foreground mb-6 leading-relaxed block"
          multiline
        />
        <a
          href={ctaLink}
          className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold shadow hover:opacity-90 transition-opacity"
        >
          <EditableText
            as="span"
            value={ctaText}
            isEditor={isEditor}
            onChange={(newVal) => onUpdateProps?.(node.id, { ctaText: newVal })}
          />
        </a>
      </div>
      <div className="relative rounded-xl overflow-hidden shadow-lg border border-border">
        <img
          src={imageURL}
          alt={headline}
          className="w-full h-auto object-cover max-h-[360px]"
          loading="lazy"
        />
      </div>
    </div>
  );
};
